import { getDefaultState } from './state.js';

/**
 * Reset facility state.
 */
export function RESET_STATE(state) {
    Object.assign(state, getDefaultState());
}

/**************
 * FACILITIES *
 *************/
export function ADD_FACILITY(state, facility) {
    if (state.facilities === null) {
        state.facilities = {};
    }
    state.facilities[facility.id] = { ...facility };
}

export function ADD_FACILITIES(state, facilities) {
    if (state.facilities === null) {
        state.facilities = {};
    }
    for (const facility of facilities) {
        state.facilities[facility.id] = { ...facility };
    }
}

export function ADD_FACILITY_GROUPS(state, groups) {
    for (const group of groups) {
        state.facilityGroups[group.id] = { ...group };
    }
}

export function UPDATE_FACILITY(state, facility) {
    state.facilities[facility.id] = { ...facility };
}

export function SET_SELECTED_FACILITY(state, facility) {
    state.selectedFacility = facility;
}

export function CLEAR_SELECTED_FACILITY(state) {
    state.selectedFacility = null;
}

export function ADD_LOADING_ASSETS_FAILED(state, facilityId) {
    state.failedAssetLoads[facilityId] = facilityId;
}

export function REMOVE_LOADING_ASSETS_FAILED(state, facilityId) {
    delete state.failedAssetLoads[facilityId];
}

/**********
 * ASSETS *
 **********/
export function ADD_ASSET(state, asset) {
    state.assets[asset.id] = { ...asset };
}

export function UPDATE_ASSET(state, asset) {
    state.assets[asset.id] = { ...asset };
}

export function UPDATE_ASSET_FIELD(state, { assetId, field, value }) {
    state.assets[assetId][field] = value;
}

export function SET_SELECTED_ASSET(state, asset) {
    state.selectedAsset = asset;
}

/**
 * Set status id with Vue for reactivity.
 * @param {Number} assetId
 * @param {Object} status - Live asset status data.
 */
export function SET_STATUS_ID(state, { assetId }) {
    // check whether assetId needs to be registered with Vue for reactivity
    if (!(assetId in state.statuses)) {
        state.statuses[assetId] = {
            status: 'unknown',
            product: null,
            cause: null
        };
    }
}

/**
 * Set status object to corresponding asset id.
 * @param {Number} assetId
 * @param {Object} status - Live asset status data.
 */
export function SET_STATUS_VALUE(state, { assetId, status }) {
    state.statuses[assetId] = { ...status };
}

/**
 * Set downtime cause for an asset.
 * @param {Number} assetId
 * @param {Number} causeId
 */
export function SET_DOWNTIME_CAUSE(state, { assetId, causeId }) {
    state.statuses[assetId]['cause'] = causeId;
}

/**********
 * SENSORS *
 **********/
export function ADD_SENSORS(state, { assetId, sensors }) {
    state.sensors[assetId] = sensors;
}
export function ADD_SENSOR(state, { assetId, sensor }) {
    if (state.sensors[assetId]) {
        state.sensors[assetId] = [...state.sensors[assetId], { ...sensor }];
    } else {
        state.sensors[assetId] = [{ ...sensor }];
    }
}
/*****************
 * ASSETS GROUPS *
 *****************/

export function ADD_ASSET_GROUP(state, group) {
    state.assetGroups[group.id] = { ...group };

    // update each asset that is part of the new group
    group.assets.forEach(assetId => {
        state.assets[assetId]['groups'] = [
            ...state.assets[assetId]['groups'],
            { id: group.id, name: group.name }
        ];
    });
}

export function UPDATE_ASSET_GROUP(state, group) {
    const prev_group = state.assetGroups[group.id];

    const removedAssetIds = prev_group.assets.filter(
        x => !group.assets.includes(x)
    );
    const addedAssetIds = group.assets.filter(
        x => !prev_group.assets.includes(x)
    );

    removedAssetIds.forEach(assetId => {
        state.assets[assetId]['groups'] = state.assets[assetId][
            'groups'
        ].filter(x => x.id !== group.id);
    });

    addedAssetIds.forEach(assetId => {
        state.assets[assetId]['groups'] = [
            ...state.assets[assetId]['groups'],
            { id: group.id, name: group.name }
        ];
    });

    state.assetGroups[group.id] = { ...group };
}

export function REMOVE_ASSET_GROUP(state, groupId) {
    const group = state.assetGroups[groupId];

    // update each asset that is part of the group
    group.assets.forEach(assetId => {
        state.assets[assetId]['groups'] = state.assets[assetId][
            'groups'
        ].filter(group => group.id !== groupId);
    });

    delete state.assetGroups[groupId];
}

/**************************
 * FILTERED ASSETS GROUPS *
 **************************/
/**
 * Add asset group selected for filter at facility
 * dashboard from the store as well.
 * @param {Object} obj
 * @param {Number} obj.facilityId - Facility Id
 * @param {Object} obj.selectedAssetGroup - Asset Group object
 */
export function ADD_SELECTED_GROUP_PER_FACILITY(
    state,
    { facilityId, selectedAssetGroup }
) {
    if (facilityId in state.storedAssetGroupsPerFacility) {
        // if facility already exists in the storedAssetGroupsPerFacility
        // state then update the existing state with newly selected asset
        // group.
        state.storedAssetGroupsPerFacility[facilityId] = [
            ...state.storedAssetGroupsPerFacility[facilityId],
            { ...selectedAssetGroup }
        ];
    } else {
        // if facility does not exist in the state, initialize the facility to
        // an empty array and update its state with the newly selected asset
        // group.
        state.storedAssetGroupsPerFacility[facilityId] = [
            { ...selectedAssetGroup }
        ];
    }
}

/**
 * Remove asset group removed in filter at facility
 * dashboard from the store as well.
 * @param {Object} obj
 * @param {Number} obj.facilityId - Facility Id
 * @param {Object} obj.removedAssetGroup - Asset Group object
 */
export function REMOVE_SELECTED_GROUP_PER_FACILITY(
    state,
    { facilityId, removedAssetGroup }
) {
    if (facilityId in state.storedAssetGroupsPerFacility) {
        // if facility exists in the state, get the index of the removed asset group
        // and then update the existing state by removing the element from the selected
        // index.
        const assetGroupIndex = state.storedAssetGroupsPerFacility[
            facilityId
        ].findIndex(assetGroup => assetGroup.id === removedAssetGroup.id);
        state.storedAssetGroupsPerFacility[facilityId] =
            state.storedAssetGroupsPerFacility[facilityId].filter(
                (_, index) => index !== assetGroupIndex
            );
    }
}

/**
 * Add asset's disconnection status to vuex store
 * @param {obj} state
 * @param {Array} disconnections - List of the disconnected assets
 */
export function ADD_DISCONNECTION(state, disconnections) {
    disconnections.assets.forEach(asset => {
        state.disconnectedAssets.set(asset, {
            assetId: asset,
            device: disconnections.device,
            start: disconnections.start
        });
    });
}

/**
 * Reset the disconnectedAssets status
 * @param {obj} state
 */
export function RESET_DISCONNECTIONS(state) {
    state.disconnectedAssets = new Map();
}

/**
 * Store assets connected to a device
 * @param {obj} state
 * @param {Array} assets - List of assets connected to a device
 */
export function ADD_ASSETS_WITH_SENSOR(state, assets) {
    assets.forEach(asset => {
        state.assetsWithSensors.add(asset.id);
    });
}

/***********
 * DEFECTS *
 ***********/

/**
 * Adds a defect to the state.
 *
 * @param {Object} state - The current state of the store.
 * @param {Object} defect - The defect to be added.
 * @param {string} defect.id - The unique identifier of the defect.
 */
export function ADD_DEFECT(state, defect) {
    state.defects[defect.id] = { ...defect };
}

/******************
 * QUALITY EVENTS *
 ******************/

/**
 * Sets the current quality event for a specific asset.
 *
 * @param {Object} state - The state object of the store.
 * @param {string} assetID - The ID of the asset.
 * @param {Object} qualityEvent - The quality event object.
 */
export function SET_QUALITY_EVENT(state, qualityEvent) {
    if (!qualityEvent) {
        return;
    }
    const assetID = qualityEvent['assetID'];
    state.qualityEvents[assetID] = { ...qualityEvent };
}

/**
 * Removes the current quality event for a specific asset.
 *
 * @param {Object} state - The state object of the store.
 * @param {string} assetID - The ID of the asset.
 */
export function REMOVE_QUALITY_EVENT(state, assetID) {
    delete state.qualityEvents[assetID];
}
